import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Menu, X, Shield, Search, User, ShoppingCart } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Services', path: '/services' },
  { label: 'Brands', path: '/brands' },
  { label: 'Verify', path: '/verify' },
  { label: 'About', path: '/about' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

const FOOTER_LINKS = [
  { label: 'Terms', path: '/terms' },
  { label: 'Privacy', path: '/privacy' },
  { label: 'Contact', path: '/contact' },
  { label: 'FAQ', path: '/faq' },
];

export default function PublicLayout() {
  const { isAuthenticated, canAccessTeam } = useAuth();
  const { itemCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'hsl(var(--bg-paper))' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-stone">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 no-underline" data-testid="primary-nav-logo">
            <Shield className="h-6 w-6 text-ink" strokeWidth={1.5} />
            <span className="font-display text-lg font-medium tracking-tight text-ink">The Fashion Detective</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6" data-testid="primary-nav-desktop">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium no-underline transition-colors duration-200 ${
                  location.pathname === link.path ? 'text-ink' : 'text-muted-foreground hover:text-ink'
                }`}
                data-testid={`primary-nav-${link.label.toLowerCase()}-link`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Cart */}
            <Link to="/order/cart" className="relative" data-testid="nav-cart-link">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="h-4 w-4" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-ink text-white text-[10px] flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>
            {isAuthenticated ? (
              <>
                {canAccessTeam && (
                  <Link to="/team" data-testid="primary-nav-team-link">
                    <Button variant="ghost" size="sm" className="text-xs uppercase tracking-wider">
                      Team
                    </Button>
                  </Link>
                )}
                <Link to="/account" data-testid="primary-nav-account-link">
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    Account
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/account/sign-in" data-testid="primary-nav-signin-link">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col gap-1 mt-6">
                  {NAV_LINKS.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="px-3 py-2 text-sm font-medium rounded-md no-underline text-ink hover:bg-secondary/60"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Separator className="my-2" />
                  {isAuthenticated ? (
                    <>
                      <Link to="/account" className="px-3 py-2 text-sm font-medium rounded-md no-underline text-ink hover:bg-secondary/60" onClick={() => setMobileOpen(false)}>Account</Link>
                      {canAccessTeam && <Link to="/team" className="px-3 py-2 text-sm font-medium rounded-md no-underline text-ink hover:bg-secondary/60" onClick={() => setMobileOpen(false)}>Team Dashboard</Link>}
                    </>
                  ) : (
                    <Link to="/account/sign-in" className="px-3 py-2 text-sm font-medium rounded-md no-underline text-ink hover:bg-secondary/60" onClick={() => setMobileOpen(false)}>Sign In</Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-stone mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-5 w-5 text-ink" strokeWidth={1.5} />
                <span className="font-display text-base font-medium text-ink">The Fashion Detective</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Expert luxury authentication and appraisal services. Protect your investment with confidence.
              </p>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Quick Links</h4>
              <div className="flex flex-col gap-2">
                {NAV_LINKS.map((link) => (
                  <Link key={link.path} to={link.path} className="text-sm text-ink no-underline hover:text-muted-foreground">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Legal & Support</h4>
              <div className="flex flex-col gap-2">
                {FOOTER_LINKS.map((link) => (
                  <Link key={link.path} to={link.path} className="text-sm text-ink no-underline hover:text-muted-foreground">
                    {link.label}
                  </Link>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                orders@thefashiondetective.com<br />
                support@thefashiondetective.com
              </p>
            </div>
          </div>
          <Separator className="my-6" />
          <p className="text-xs text-muted-foreground text-center">
            &copy; {new Date().getFullYear()} The Fashion Detective. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
